import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useSelector } from "react-redux";
import { Box, makeStyles, Grid, Typography, Tooltip, withStyles } from "@material-ui/core";

import IconCard from "../../iconCard";
import { COLORS } from "../../../../../theme/colors";
import { USER_ROLES } from "../../../../../services/constants";
import { extractAddressInfo } from "../../../../../services/utils";
import { AddressAutoComplete, Image, TextInput } from "../../../../../components/base";
import share from "../../../../../assets/images/icons/shareIcon.svg";
import officeIcon from "../../../../../assets/images/icons/office.svg";
import officeIconSelected from "../../../../../assets/images/icons/workspace/office-selected.svg";
import residentialIcon from "../../../../../assets/images/icons/residential.svg";
import residentialIconSelected from "../../../../../assets/images/icons/workspace/residental-selected.svg";
import logisticIcon from "../../../../../assets/images/icons/logistics.svg";
import logisticIconSelected from "../../../../../assets/images/icons/workspace/logistic-selected.svg";
import retailIcon from "../../../../../assets/images/icons/retail.svg";
import retailIconSelected from "../../../../../assets/images/icons/workspace/retail-selected.svg";
import hospitalityIcon from "../../../../../assets/images/icons/hospitality.svg";
import hospitalityIconSelected from "../../../../../assets/images/icons/workspace/hospitality-selected.svg";
import DataCenterIcon from "../../../../../assets/images/icons/data-centers.svg";
import DataCenterIconSelected from "../../../../../assets/images/icons/workspace/dataCenter-selected.svg";

const icons = [
  {
    id: "OFFICE",
    icon: officeIcon,
    iconSelected: officeIconSelected,
    text: "Office",
  },
  {
    id: "RESIDENTIAL",
    icon: residentialIcon,
    iconSelected: residentialIconSelected,
    text: "Residential",
  },
  {
    id: "LOGISTIC",
    icon: logisticIcon,
    iconSelected: logisticIconSelected,
    text: "Logistics",
  },
  {
    id: "RETAIL",
    icon: retailIcon,
    iconSelected: retailIconSelected,
    text: "Retail",
  },
  {
    id: "HOSPITALITY",
    icon: hospitalityIcon,
    iconSelected: hospitalityIconSelected,
    text: "Hospitality",
  },
  {
    id: "DATA-CENTERS",
    icon: DataCenterIcon,
    iconSelected: DataCenterIconSelected,
    text: "Data-Centers",
  },
];

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: 30,
    fontWeight: 700,
    marginBottom: 50,
  },
  root: {
    flexGrow: 1,
    paddingBottom: 100,
  },
  paper: {
    padding: theme.spacing(0.5),
    fontWeight: 400,
    color: COLORS.blackShade,
  },
  clusterText: {
    fontSize: 14,
    marginTop: 10,
  },
  box: {
    display: "flex",
  },
  iconText: {
    display: "flex",
    justifyContent: "space-between",
    width: 640,
    "& .MuiOutlinedInput-input": {
      padding: "11.5px 14px",
    },
  },
  img: {
    padding: 10,
    cursor: "pointer",
  },
  input: {
    width: 600,
    "& .MuiOutlinedInput-input": {
      padding: "11.5px 14px",
    },
  },
  IconBox: {
    display: "flex",
  },
  iconContainer: {
    marginTop: 70,
    marginBottom: 30,
  },
  coloredText: {
    margin: `0 ${theme.spacing(1)}px`,
    color: COLORS.purple,
    cursor: "pointer",
    textDecoration: "none",
  },
  icon: {
    color: COLORS.orange,
    marginRight: theme.spacing(1),
    padding: 20,
  },
  buildingValues: {
    fontSize: 18,
  },
  addressText: {
    fontSize: 18,
    width: 585,
    height: 38,
    lineHeight: 3,
    borderRadius: 4,
    paddingLeft: 10,
    display: "block",
    backgroundColor: "white",
    border: `1px solid ${COLORS.darks[0][10]}`,
    "&:focus-visible": {
      outline: `2px solid ${COLORS.purple}`,
    },
    "& .MuiOutlinedInput-input": {
      padding: "11.5px 14px",
    },
  },
  showNote: {
    display: "flex",
    borderRadius: 4,
    marginTop: 40,
    background: COLORS.lightOrange,
  },
}));

const GlobalCss = withStyles(() => ({
  "@global": {
    ".pac-container": {
      zIndex: "9999 !important",
    },
  },
}))(() => null);

const General = ({ building, onBuildingChange }) => {
  const classes = useStyles();
  const { cluster } = useSelector((state) => state.selectCSC);
  const [showNote, setShowNote] = useState(false);
  const [address, setAddress] = useState(building.address);

  useEffect(() => {
    const draftData = building.draft_data;
    if (
      (draftData.name && building.name !== draftData.name) ||
      (draftData.address && building.address !== draftData.address)
    ) {
      setShowNote(true);
    } else {
      setShowNote(false);
    }
  }, [building]);

  const handleLongLat = (addr) => {
    const { address: formattedAddr, country, postalCode } = extractAddressInfo(addr);
    setAddress(formattedAddr);
    onBuildingChange(null, null, {
      address: formattedAddr,
      lat: addr.geometry.location.lat(),
      long: addr.geometry.location.lng(),
      zip_code: postalCode,
      country,
    });
  };

  return (
    <Box>
      <Typography className={classes.heading}>
        First, Let’s verify the details we already have
      </Typography>
      <Box className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Typography variant="h4" className={classes.paper}>
              Building name:
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Box className={classes.input}>
              <TextInput
                fullWidth
                value={building.name}
                onChange={(v) => onBuildingChange("name", v)}
                style={{ background: COLORS.white }}
                type="text"
                containerStyle={classes.box}
                inputRootStyle={classes.buildingValues}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Typography variant="h4" className={classes.paper}>
              Address:
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Box className={classes.iconText}>
              <GlobalCss />
              <AddressAutoComplete
                onSeleted={handleLongLat}
                value={address}
                onChange={(v) => {
                  setAddress(v);
                }}
                cssStyle={classes.addressText}
              />
              <Box className={classes.img}>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${building.lat}%2C${building.long}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Image src={share} height={20} weight={20} />
                </a>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} className={classes.iconContainer}>
          <Grid item xs>
            <Typography variant="h4" className={classes.paper}>
              Real estate segment:
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Box className={classes.IconBox}>
              {building.segments && building.segments.length
                ? icons.map((data) => {
                    return (
                      <Tooltip
                        title={
                          <h3>To change the segment of the building, update the meter segment</h3>
                        }
                        key={data.id}
                      >
                        <div style={{ display: "flex", width: "110px" }}>
                          <IconCard
                            key={data.id}
                            id={data.id}
                            selected={building.segments?.includes(data.id)}
                            src={
                              building.segments?.includes(data.id) ? data.iconSelected : data.icon
                            }
                            text={data.text}
                            style={{
                              background: building.segments?.includes(data.id)
                                ? COLORS.purple
                                : COLORS.white,
                            }}
                            onClick={() => {
                              console.log("can't change segments provided from smart meters");
                            }}
                          />
                        </div>
                      </Tooltip>
                    );
                  })
                : icons.map((data) => {
                    return (
                      <IconCard
                        key={data.id}
                        id={data.id}
                        selected={building.segment === data.id}
                        src={building.segment === data.id ? data.iconSelected : data.icon}
                        text={data.text}
                        style={{
                          background: building.segment === data.id ? COLORS.purple : COLORS.white,
                        }}
                        onClick={() => onBuildingChange("segment", data.id)}
                      />
                    );
                  })}
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs>
            <Typography variant="h4" className={classes.paper}>
              Building cluster:
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Box className={classes.clusterText}>
              {building.clusters?.find((c) => c.parent !== null)?.name}
              {cluster?.user_role === USER_ROLES.super_pm && (
                <>
                  <Link to="/workspace/settings/portfolio-profile" className={classes.coloredText}>
                    Edit
                  </Link>
                  <em style={{ color: COLORS.grayShade }}>(closes this window)</em>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        {showNote && (
          <Box className={classes.showNote}>
            <AiOutlineInfoCircle className={classes.icon} size={20} />
            <Typography style={{ marginTop: 15, fontSize: 18 }}>
              Note- The information is different than the registration info
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default General;
